import type { RefObject } from "react";
import { useTranslation } from "react-i18next";
import type { SocialCommentThreadResponse } from "@multi-chat/shared";
import {
  ArrowLeft as ArrowLeftIcon,
  PanelRightClose as PanelRightCloseIcon,
} from "lucide-react";
import { ChannelIdentityBadge } from "../../../components/channel/ChannelIdentityBadge.js";
import { LtrValue } from "../../../components/common/LtrValue.js";
import { Button } from "../../../components/ui/button/index.js";
import { SimpleTooltip } from "../../../components/ui/tooltip/index.js";
import { cn } from "../../../lib/utils.js";
import { formatConversationFullDate } from "../conversation-time.js";
import { InboxIconButton } from "../InboxIconButton.js";
import {
  isBusinessSelfAuthor,
  resolveCommentAuthorLabel,
} from "./comment-identity.js";

export function CommentDetailsPane({
  data,
  workflowPending,
  onMarkHandled,
  onReopen,
  onOpenConversation,
  closeControl,
  onClose,
  closeButtonRef,
  className,
}: {
  data: SocialCommentThreadResponse;
  workflowPending: boolean;
  onMarkHandled: () => Promise<boolean>;
  onReopen: () => Promise<boolean>;
  onOpenConversation?: (conversationId: number) => void;
  closeControl: "collapse" | "back";
  onClose: () => void;
  closeButtonRef?: RefObject<HTMLButtonElement | null>;
  className?: string;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const comment = data.selectedComment;
  const handled = comment.handlingStatus === "HANDLED";
  const identity = resolveCommentAuthorLabel({
    authorDisplayName: comment.authorDisplayName,
    authorUsername: comment.authorUsername,
    unknownLabel: t("inbox.comments.author.unknown"),
  });
  const businessSelf = isBusinessSelfAuthor(comment.authorType);
  const closeLabel =
    closeControl === "back" ? t("inbox.details.backToThread") : t("inbox.details.collapse");

  const closeButton = (
    <SimpleTooltip content={closeLabel}>
      <InboxIconButton
        ref={closeButtonRef}
        aria-label={closeLabel}
        aria-controls={closeControl === "collapse" ? "post-comments-details" : undefined}
        onClick={onClose}
      >
        {closeControl === "back" ? (
          <ArrowLeftIcon size={18} className="rtl:rotate-180" aria-hidden="true" />
        ) : (
          <PanelRightCloseIcon size={18} className="rtl:-scale-x-100" aria-hidden="true" />
        )}
      </InboxIconButton>
    </SimpleTooltip>
  );

  return (
    <aside
      className={cn("flex h-full min-h-0 flex-col overflow-hidden bg-surface", className)}
      aria-label={t("inbox.comments.details.panelLabel")}
    >
      <header className="flex min-h-[60px] shrink-0 items-center gap-1.5 border-b border-inbox-line-soft ps-4 pe-2.5">
        {closeControl === "back" ? <span className="-ms-2">{closeButton}</span> : null}
        <h2 className="m-0 min-w-0 flex-1 truncate text-[0.90625rem] font-semibold tracking-[-0.012em] text-inbox-strong rtl:tracking-normal">
          {t("inbox.comments.details.panelLabel")}
        </h2>
        {closeControl === "collapse" ? closeButton : null}
      </header>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
        <section className="rounded-[12px] border border-inbox-line px-3 py-2.5">
          <h3 className="m-0 mb-2 text-[0.75rem] font-semibold tracking-wide text-inbox-muted uppercase">
            {t("inbox.comments.details.commenter")}
          </h3>
          <div className="flex items-center gap-2">
            <ChannelIdentityBadge platformType={comment.platformType} size="row" />
            <div className="min-w-0 flex-1">
              <p
                className="m-0 truncate text-[0.84375rem] font-semibold text-inbox-strong"
                dir="auto"
              >
                {identity.isHandleOnly ? (
                  <LtrValue className="truncate">{identity.primary}</LtrValue>
                ) : (
                  identity.primary
                )}
                {businessSelf ? (
                  <span className="ms-1 font-medium text-inbox-muted">
                    {t("inbox.comments.author.meSuffix")}
                  </span>
                ) : null}
              </p>
              {identity.secondaryUsername ? (
                <p className="m-0 text-[0.75rem] text-inbox-muted">
                  <LtrValue>@{identity.secondaryUsername}</LtrValue>
                </p>
              ) : (
                <p className="m-0 text-[0.75rem] text-inbox-muted">
                  {t(`inbox.comments.authorType.${comment.authorType}`)}
                </p>
              )}
            </div>
          </div>
          {data.existingConversation && onOpenConversation ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2.5"
              onClick={() => onOpenConversation(data.existingConversation!.conversationId)}
            >
              {t("inbox.comments.details.openConversation")}
            </Button>
          ) : null}
        </section>

        <section className="rounded-[12px] border border-inbox-line px-3 py-2.5">
          <h3 className="m-0 mb-2 text-[0.75rem] font-semibold tracking-wide text-inbox-muted uppercase">
            {t("inbox.comments.details.handling")}
          </h3>
          <p className="m-0 text-[0.84375rem] font-semibold text-inbox-strong">
            {handled ? t("inbox.comments.status.handled") : t("inbox.comments.status.open")}
          </p>
          {handled && comment.handledAt ? (
            <p className="m-0 mt-1 text-[0.75rem] text-inbox-muted">
              {t("inbox.comments.details.handledAt", {
                when: formatConversationFullDate(comment.handledAt, i18n.language),
              })}
              {comment.handledReason
                ? ` · ${t(`inbox.comments.details.handledReason.${comment.handledReason}`)}`
                : null}
            </p>
          ) : null}
          <div className="mt-2.5">
            {handled ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={workflowPending}
                onClick={() => {
                  void onReopen();
                }}
              >
                {t("inbox.comments.details.reopen")}
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                disabled={workflowPending}
                onClick={() => {
                  void onMarkHandled();
                }}
              >
                {t("inbox.comments.details.markHandled")}
              </Button>
            )}
          </div>
        </section>

        <section className="rounded-[12px] border border-inbox-line px-3 py-2.5">
          <h3 className="m-0 mb-2 text-[0.75rem] font-semibold tracking-wide text-inbox-muted uppercase">
            {t("inbox.comments.details.publicReply")}
          </h3>
          <p className="m-0 text-[0.8125rem] text-inbox-strong">
            {comment.publicReplyCount > 0
              ? t("inbox.comments.details.publicReplyCount", { count: comment.publicReplyCount })
              : t("inbox.comments.details.publicReplyNone")}
          </p>
        </section>

        <section className="rounded-[12px] border border-inbox-line px-3 py-2.5">
          <h3 className="m-0 mb-2 text-[0.75rem] font-semibold tracking-wide text-inbox-muted uppercase">
            {t("inbox.comments.details.privateReply")}
          </h3>
          {comment.privateReplySentAt ? (
            <>
              <p className="m-0 text-[0.8125rem] text-inbox-strong">
                {t("inbox.comments.details.privateReplySent", {
                  when: formatConversationFullDate(comment.privateReplySentAt, i18n.language),
                })}
              </p>
              {comment.privateReplyConversationId != null && onOpenConversation ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2.5"
                  onClick={() => onOpenConversation(comment.privateReplyConversationId!)}
                >
                  {t("inbox.comments.details.openPrivateReplyConversation")}
                </Button>
              ) : null}
            </>
          ) : (
            <p className="m-0 text-[0.8125rem] text-inbox-muted">
              {data.privateReplyEligibility.allowed
                ? t("inbox.comments.details.privateReplyEligible")
                : t(
                    `inbox.comments.composer.privateBlocked.${data.privateReplyEligibility.reasonCode}`,
                  )}
            </p>
          )}
        </section>
      </div>
    </aside>
  );
}
